/* @flow */

import { Formik, Form, Field } from 'formik';
import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import * as Yup from 'yup';

import styles from 'src/shared/app/authentication/component/base/AuthenticationBase.style';
import AuthenticationFormHeader from 'src/shared/app/authentication/component/base/AuthenticationFormHeader';
import authMessages from 'src/shared/app/authentication/l10n/authenticationL10n';
import messages from 'src/shared/app/authentication/l10n/loginLinkL10n';
import { usernameFieldMaxLength } from 'src/shared/app/authentication/settings/authenticationSettings';
import FormikFormGroup from 'src/shared/app/base/component/data-entry/form/FormikFormGroup';
import useRoutes from 'src/shared/app/base/route/hook/useRoutes';
import { contactRoute } from 'src/shared/app/base/route/setting/routeSettings';
import SecondaryButton from 'src/shared/ui/component/button/SecondaryButton';
import DiscreteLink from 'src/shared/ui/component/link/DiscreteLink';

type Props = {
  email: ?string,
  eventName: string,
  handleSubmit: (Object) => Promise<any>,
};

const useStyles = createUseStyles(styles);

const LoginLinkSectionDumb = ({
  email,
  eventName,
  handleSubmit,
}: Props): React.Node => {
  const schema = Yup.object().shape({
    email: Yup.string().required().email(),
  });

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const intl = useIntl();
  const [contactPath] = useRoutes(contactRoute);

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <Formik
      initialValues={{ email: email || '' }}
      validationSchema={schema}
      validateOnBlur={true}
      onSubmit={(values, { setSubmitting, setStatus }) =>
        handleSubmit(values).catch(({ errors }) => {
          setSubmitting(false);

          const { root, email } = errors || {};

          if (root || email) {
            setStatus(root || email);
          }
        })
      }
    >
      {({ status, values, isSubmitting }) => {
        const helperElt = status && (
          <div className={classes.support}>
            <DiscreteLink href={contactPath}>
              {intl.formatMessage(authMessages.contact)}
            </DiscreteLink>
          </div>
        );

        return (
          <Form className={classes.form} noValidate>
            <AuthenticationFormHeader withError={!!status}>
              {status || intl.formatMessage(messages.legend, { eventName })}
            </AuthenticationFormHeader>
            <Field
              id="email"
              name="email"
              type="email"
              component={FormikFormGroup}
              componentOpts={{
                label: intl.formatMessage(authMessages.emailLabel),
              }}
              maxLength={usernameFieldMaxLength}
              required
              autoFocus
            />
            <div className={classes.submit}>
              <SecondaryButton
                isDisabled={!values.email}
                isSubmitting={isSubmitting}
              >
                {intl.formatMessage(messages.submit)}
              </SecondaryButton>
            </div>
            {helperElt}
          </Form>
        );
      }}
    </Formik>
  );
};

export default compose(React.memo)(LoginLinkSectionDumb);
